<template>
  <!-- Shared top bar for every logged-in surface (Dashboard, Team Calendar, admin
       panel). Fixed h-14 (56px, same as Atlassian's top nav) so the bar is the
       same height everywhere — previously each page had its own header markup and
       they drifted (Dashboard's two-line title made it ~63-69px vs admin's 56px).
       Owns the full Actions/user menu set and the modals those items open, so a
       new menu item only has to be added once. -->
  <!-- z-50: must beat the calendar body's own sticky internals (DayView/WeekView's
       resize-handle corner is z-40) — this header is position:sticky, so it forms its
       own stacking context and a lower z-index would let those elements render over
       the whole header, dropdowns included. -->
  <header class="sticky top-0 z-50 flex-shrink-0 bg-header-bg dark:bg-header-bg-dark border-b border-header-border dark:border-header-border-dark">
    <div class="flex items-center gap-2 h-14 px-4">
      <slot name="leading" />

      <img v-if="logoUrl" :src="logoUrl" class="h-8 w-8 object-contain flex-shrink-0" alt="" />
      <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-6 w-6 flex-shrink-0">
        <path fill="#1b62da" d="M19 9c0-1.65-1.35-3-3-3s-3 1.35-3 3v9c0 .55-.45 1-1 1s-1-.45-1-1V6c0-1.65-1.35-3-3-3S5 4.35 5 6v5H2v2h4c.55 0 1-.45 1-1V6c0-.55.45-1 1-1s1 .45 1 1v12c0 1.65 1.35 3 3 3s3-1.35 3-3V9c0-.55.45-1 1-1s1 .45 1 1v3c0 .55.45 1 1 1h4v-2h-3z"></path>
      </svg>

      <h1 class="min-w-0 flex items-center gap-1.5 text-base font-semibold text-fg-base dark:text-fg-base-dark">
        <span class="truncate">{{ title }}</span>
        <span v-if="demoModeActive" class="px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[10px] font-semibold leading-none flex-shrink-0">Demo</span>
      </h1>

      <div class="flex-1" />

      <HeaderControls
        :current-user="currentUser && { ...currentUser, name: previewName, permissions: effectivePermissions }"
        :is-dark-mode="isDarkMode"
        @toggle-theme="toggleTheme"
        @logout="$emit('logout')"
      >
        <template #menu-items="{ close }">
          <button
            @click="close(); showEditProfileModal = true"
            data-tour="edit-profile-menu-item"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <IdentificationIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            Edit Profile
          </button>
          <button
            @click="close(); showPreferencesModal = true"
            data-tour="preferences-menu-item"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <AdjustmentsHorizontalIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            Preferences
          </button>
          <!-- Help runs the Dashboard's coach-mark tour, whose steps point at
               Dashboard-only elements — so only the Dashboard opts in. -->
          <button
            v-if="showHelp"
            @click="close(); $emit('help')"
            data-tour="help-menu-item"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <QuestionMarkCircleIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            Help
          </button>
          <DemoModePanel v-if="currentUser?.permissions?.includes('admin')" />
        </template>

        <template #action-items="{ close }">
          <button
            v-for="item in navItems"
            :key="item.to"
            @click="close(); router.push(item.to)"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <component :is="item.icon" class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            {{ item.label }}
          </button>
          <button
            @click="close(); newLesson()"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <PlusCircleIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            New Lesson
          </button>
          <button
            v-if="effectivePermissions.includes('admin')"
            @click="close(); newMembership()"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <CreditCardIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            New Membership Offer
          </button>
          <!-- Opens (or switches to) the workspace sidebar's Signups tab -->
          <button
            v-if="hasSignupsPermission && adminSidebar.available.value"
            @click="close(); openSignups()"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <UserPlusIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            Lesson Signups
          </button>
          <button
            @click="close(); showTeacherCancellationModal = true"
            class="w-full flex items-center px-4 py-2 text-sm text-fg-secondary dark:text-fg-secondary-dark transition-colors"
          >
            <NoSymbolIcon class="h-4 w-4 mr-3 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
            Mark Time Off
          </button>
        </template>
      </HeaderControls>
    </div>
  </header>

  <EditProfileModal
    v-if="showEditProfileModal"
    :start-expanded="tourExpandAvailability"
    @close="showEditProfileModal = false; tourExpandAvailability = false"
  />
  <PreferencesModal
    v-if="showPreferencesModal"
    :focus="preferencesFocus"
    @close="showPreferencesModal = false; preferencesFocus = null"
    @saved="$emit('preferences-saved', $event)"
    @change-password="showPreferencesModal = false; showChangePasswordModal = true"
  />
  <ChangePasswordModal v-if="showChangePasswordModal" @close="showChangePasswordModal = false" />
  <TeacherCancellationModal v-if="showTeacherCancellationModal" @close="showTeacherCancellationModal = false; notifyLessonsChanged()" />
  <LessonCreateModal
    v-if="showCreateLessonModal"
    v-bind="lessonCreateProps()"
    @close="showCreateLessonModal = false"
    @saved="showCreateLessonModal = false"
  />
  <MembershipCreateModal
    v-if="showCreateMembershipModal"
    @close="showCreateMembershipModal = false"
    @saved="showCreateMembershipModal = false"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  IdentificationIcon,
  AdjustmentsHorizontalIcon,
  QuestionMarkCircleIcon,
  HomeIcon,
  Cog6ToothIcon,
  PlusCircleIcon,
  NoSymbolIcon,
  CreditCardIcon,
  UserPlusIcon,
} from '@heroicons/vue/24/outline'
import HeaderControls from './HeaderControls.vue'
import DemoModePanel from './DemoModePanel.vue'
import EditProfileModal from './EditProfileModal.vue'
import PreferencesModal from './PreferencesModal.vue'
import ChangePasswordModal from './ChangePasswordModal.vue'
import TeacherCancellationModal from './TeacherCancellationModal.vue'
import LessonCreateModal from './LessonCreateModal.vue'
import MembershipCreateModal from './MembershipCreateModal.vue'
import { useTheme } from '../../composables/useTheme'
import { useTenantBranding } from '../../composables/useTenantBranding'
import { useDemoMode } from '../../composables/useDemoMode'
import { useAdminSidebar } from '../../composables/useAdminSidebar.js'
import { easternToday, localIso, notifyLessonsChanged } from '../../composables/useCalendarData.js'

const props = defineProps({
  title: { type: String, required: true },
  currentUser: { type: Object, default: null },
  showHelp: { type: Boolean, default: false },
})
defineEmits(['logout', 'help', 'preferences-saved'])

const router = useRouter()
const route = useRoute()
const { logoUrl } = useTenantBranding()
const { isDarkMode, toggleTheme, initTheme } = useTheme()

const { active: demoModeActive } = useDemoMode()
const previewName = useDemoMode().previewNameFor(() => props.currentUser?.name)
const effectivePermissions = useDemoMode().effectivePermissionsFor(() => props.currentUser?.permissions)
const hasSignupsPermission = computed(() =>
  effectivePermissions.value.includes('admin') || effectivePermissions.value.includes('lesson_signups')
)

// Links to the other top-level surfaces, minus whichever one you're already on.
const navItems = computed(() => {
  const onAdmin = route.path.startsWith('/admin')
  const items = []
  if (onAdmin) items.push({ to: '/', label: 'Dashboard', icon: HomeIcon })
  if (hasSignupsPermission.value && !onAdmin) items.push({ to: '/admin', label: 'Workspace', icon: Cog6ToothIcon })
  return items
})

const showEditProfileModal = ref(false)
const showPreferencesModal = ref(false)
const showChangePasswordModal = ref(false)
const showTeacherCancellationModal = ref(false)
const showCreateLessonModal = ref(false)
const showCreateMembershipModal = ref(false)
// Admin pages open these in the admin sidebar; elsewhere (teacher dashboard) as popups.
const adminSidebar = useAdminSidebar()
const lessonCreateProps = () => ({
  initialDate:       localIso(easternToday()),
  lockedTeacherName: hasSignupsPermission.value ? null : props.currentUser?.name,
  lockedTeacherId:   hasSignupsPermission.value ? null : props.currentUser?.id,
  canManageSignups:  hasSignupsPermission.value,
})
function newLesson() {
  if (adminSidebar.available.value) adminSidebar.open('lesson-create', lessonCreateProps())
  else showCreateLessonModal.value = true
}
function newMembership() {
  if (adminSidebar.available.value) adminSidebar.open('membership-create')
  else showCreateMembershipModal.value = true
}
function openSignups() {
  adminSidebar.open('signups', { isAdmin: effectivePermissions.value.includes('admin') })
}

// Set true only by the tour's own 'tour:expand-availability' event right before it
// opens Edit Profile — passed through as EditProfileModal's startExpanded prop.
// Reset on close so a real, non-tour open afterward doesn't start pre-expanded.
const tourExpandAvailability = ref(false)
const handleTourExpandAvailability = () => { tourExpandAvailability.value = true }

// ?prefs=notifications (staff email footers, the SMS opt-in page's "Back to
// notification settings") opens Preferences scrolled to Notifications, then
// drops the query so a reload doesn't reopen it.
const preferencesFocus = ref(null)
watch(() => route.query.prefs, (prefs) => {
  if (!prefs) return
  preferencesFocus.value = prefs
  showPreferencesModal.value = true
  const { prefs: _, ...query } = route.query
  router.replace({ query })
}, { immediate: true })

onMounted(() => {
  initTheme()
  window.addEventListener('tour:expand-availability', handleTourExpandAvailability)
})
onUnmounted(() => {
  window.removeEventListener('tour:expand-availability', handleTourExpandAvailability)
})
</script>
